import type { CatalogEvent } from "@/features/catalog/mediators/catalogEvents";
import type { SortType } from "@/features/catalog/stores/catalogStore";
import { createMediator, type Mediator } from "@/shared/lib/componentHierarchy";

export type CatalogDisplayType = "catalog" | "list";

export type CatalogSortModalState = {
  readonly displayType: CatalogDisplayType;
};

export type CatalogSortModalEvent = Extract<
  CatalogEvent,
  {
    type:
      | "catalog/sort-modal-display-changed"
      | "catalog/sort-modal-sort-selected"
      | "catalog/sort-modal-close-requested";
  }
>;

export type CatalogSortModalEffect =
  | {
      readonly type: "apply";
      readonly sort: SortType;
      readonly displayType: CatalogDisplayType;
    }
  | { readonly type: "close" };

export function isCatalogSortModalEvent(
  event: CatalogEvent,
): event is CatalogSortModalEvent {
  return (
    event.type === "catalog/sort-modal-display-changed" ||
    event.type === "catalog/sort-modal-sort-selected" ||
    event.type === "catalog/sort-modal-close-requested"
  );
}

export function createCatalogSortModalMediator(
  displayType: CatalogDisplayType,
  runEffect: (effect: CatalogSortModalEffect) => void,
): Mediator<
  CatalogSortModalState,
  CatalogSortModalEvent,
  CatalogSortModalEffect
> {
  return createMediator<
    CatalogSortModalState,
    CatalogSortModalEvent,
    CatalogSortModalEffect
  >(
    { displayType },
    (state, event) => {
      switch (event.type) {
        case "catalog/sort-modal-display-changed":
          return {
            status: "handled",
            state: { displayType: event.displayType },
          };
        case "catalog/sort-modal-sort-selected":
          return {
            status: "handled",
            state,
            effects: [
              {
                type: "apply",
                sort: event.sort,
                displayType: state.displayType,
              },
            ],
          };
        case "catalog/sort-modal-close-requested":
          return { status: "handled", state, effects: [{ type: "close" }] };
      }
    },
    { runEffect },
  );
}
